import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, CalendarRange, Clock, Shuffle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/format";
import { plan, todayIso, fmtDay, STATI } from "@/lib/cantieri";
import { LinkInfo } from "@/components/cantieri/FattureCollegate";

const blank = () => ({ nome: "", contact_id: "", cliente_nome: "", quote_id: "", quote_numero: "", invoice_id: "", invoice_numero: "", indirizzo: "", data_inizio: todayIso(),
  data_fine: "", calcolo: "libero", num_operai: 2, ore_giorno: 8, ore_previste: "", operai_ids: [], importo_lavori: "", stato: "programmato", note: "" });
const F = ({ label, children, className = "" }) => <div className={`space-y-1.5 ${className}`}><Label className="text-xs">{label}</Label>{children}</div>;

const Mode = ({ value, onChange }) => (
  <div className="inline-flex flex-wrap rounded-lg border border-border bg-secondary/50 p-0.5" data-testid="cantiere-calc-mode">
    {[["libero", Shuffle, "Libero, giorno per giorno"], ["date", CalendarRange, "Operai fissi tra due date"], ["ore", Clock, "Da ore totali"]].map(([k, Icon, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`cantiere-calc-${k}`}
        className={`px-3 py-1.5 text-sm rounded-md font-medium flex items-center gap-1.5 transition-colors ${value === k ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
        <Icon className="w-4 h-4" /> {l}
      </button>
    ))}
  </div>
);

export default function CantiereDialog({ open, onClose, onSaved, initial, operai = [], contacts = [], quotes = [], invoices = [] }) {
  const [f, setF] = useState(blank());
  const [link, setLink] = useState(null);
  const [saving, setSaving] = useState(false);
  const edit = !!initial?.id;
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  useEffect(() => { if (open) setF({ ...blank(), ...(initial || {}), ore_previste: initial?.ore_previste ?? "", importo_lavori: initial?.importo_lavori ?? "" }); }, [open, initial]);
  const p = useMemo(() => plan(f), [f]);
  useEffect(() => {
    if (!open || (!f.quote_id && !f.invoice_id)) { setLink(null); return; }
    api.get("/cantieri/collegamento", { params: { quote_id: f.quote_id || undefined, invoice_id: f.invoice_id || undefined } })
      .then((r) => setLink(r.data)).catch(() => setLink(null));
  }, [open, f.quote_id, f.invoice_id]);
  const pickInvoice = (id) => {
    const i = invoices.find((x) => x.id === id);
    const q = quotes.find((x) => x.id === i?.quote_id);
    if (i) set({ invoice_id: id, invoice_numero: i.numero, contact_id: i.contact_id || f.contact_id || "", cliente_nome: i.cliente_nome, importo_lavori: i.totale,
      quote_id: q ? q.id : f.quote_id, quote_numero: q ? q.numero : f.quote_numero, nome: f.nome || i.oggetto || `Cantiere ${i.cliente_nome}` });
  };

  const pickQuote = (id) => {
    const q = quotes.find((x) => x.id === id);
    if (q) set({ quote_id: id, quote_numero: q.numero, contact_id: q.contact_id || "", cliente_nome: q.cliente_nome, importo_lavori: q.totale, nome: f.nome || q.oggetto || `Cantiere ${q.cliente_nome}` });
  };
  const pickContact = (id) => { const c = contacts.find((x) => x.id === id); set({ contact_id: id, cliente_nome: c?.ragione_sociale || "", indirizzo: f.indirizzo || c?.indirizzo || "" }); };
  const toggleOp = (id) => setF((x) => {
    const ids = x.operai_ids.includes(id) ? x.operai_ids.filter((v) => v !== id) : [...x.operai_ids, id];
    return { ...x, operai_ids: ids, num_operai: ids.length || x.num_operai };
  });

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, contact_id: f.contact_id || null, quote_id: f.quote_id || null, invoice_id: f.invoice_id || null, num_operai: Number(f.num_operai) || 1, ore_giorno: Number(f.ore_giorno) || 8,
        ore_previste: f.ore_previste === "" ? null : Number(f.ore_previste), importo_lavori: f.importo_lavori === "" ? null : Number(f.importo_lavori),
        data_fine: f.calcolo === "ore" ? "" : f.data_fine };
      const r = edit ? await api.put(`/cantieri/${initial.id}`, body) : await api.post("/cantieri", body);
      toast.success(edit ? "Cantiere aggiornato" : "Cantiere creato e inserito in agenda");
      onSaved(r.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="cantiere-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{edit ? "Modifica cantiere" : "Nuovo cantiere"}</DialogTitle>
          <DialogDescription>Domeniche escluse in automatico. Il cantiere compare in agenda in arancione.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Nome cantiere *" className="sm:col-span-2"><Input value={f.nome} onChange={(e) => set({ nome: e.target.value })} placeholder="Es. Rifacimento bagno Rossi" data-testid="cantiere-name-input" /></F>
            <F label="Da preventivo (facoltativo)">
              <Select value={f.quote_id || ""} onValueChange={pickQuote}>
                <SelectTrigger data-testid="cantiere-quote-select"><SelectValue placeholder="Scegli preventivo…" /></SelectTrigger>
                <SelectContent>{quotes.map((q) => <SelectItem key={q.id} value={q.id}>N. {q.numero} · {q.cliente_nome}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Da fattura (facoltativo)">
              <Select value={f.invoice_id || ""} onValueChange={pickInvoice}>
                <SelectTrigger data-testid="cantiere-invoice-select"><SelectValue placeholder="Scegli fattura…" /></SelectTrigger>
                <SelectContent>{invoices.map((i) => <SelectItem key={i.id} value={i.id}>N. {i.numero} · {i.cliente_nome}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            {link && <LinkInfo l={link} />}
            <F label="Cliente">
              <Select value={f.contact_id || ""} onValueChange={pickContact}>
                <SelectTrigger data-testid="cantiere-contact-select"><SelectValue placeholder={f.cliente_nome || "Seleziona"} /></SelectTrigger>
                <SelectContent>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Indirizzo cantiere"><Input value={f.indirizzo || ""} onChange={(e) => set({ indirizzo: e.target.value })} data-testid="cantiere-address-input" /></F>
          </div>

          <div className="rounded-xl border border-border p-4 space-y-4 bg-secondary/20">
            <Mode value={f.calcolo} onChange={(calcolo) => set({ calcolo })} />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <F label="Data inizio *"><DateInput value={f.data_inizio} onChange={(e) => set({ data_inizio: e.target.value })} data-testid="cantiere-start-input" /></F>
              {f.calcolo !== "ore"
                ? <F label={f.calcolo === "libero" ? "Data fine (indicativa)" : "Data fine *"}><DateInput value={f.data_fine || ""} onChange={(e) => set({ data_fine: e.target.value })} data-testid="cantiere-end-input" /></F>
                : <F label="Ore totali previste *"><Input type="number" min="1" value={f.ore_previste} onChange={(e) => set({ ore_previste: e.target.value })} data-testid="cantiere-hours-input" /></F>}
              {f.calcolo === "libero"
                ? <F label="Ore stimate in tutto (facoltativo)" className="col-span-2"><Input type="number" min="0" value={f.ore_previste} onChange={(e) => set({ ore_previste: e.target.value })} placeholder="Es. 300" data-testid="cantiere-estimate-input" /></F>
                : <>
                  <F label="N. operai"><Input type="number" min="1" value={f.num_operai} onChange={(e) => set({ num_operai: e.target.value })} data-testid="cantiere-workers-input" /></F>
                  <F label="Ore al giorno"><Input type="number" min="1" max="24" step="0.5" value={f.ore_giorno} onChange={(e) => set({ ore_giorno: e.target.value })} data-testid="cantiere-daily-hours-input" /></F>
                </>}
            </div>
            <div className="text-sm rounded-lg bg-card border border-border px-3 py-2.5" data-testid="cantiere-plan-preview">
              {p?.libero ? <><b>{p.giorni}</b> {p.giorni === 1 ? "giorno lavorativo" : "giorni lavorativi"} (lun–sab) · fine <b>{fmtDay(p.data_fine)}</b> · quanti operai e quante ore lo segni <b>giorno per giorno</b> in «Ore lavorate»{p.ore_previste ? <> · stima <b>{p.ore_previste.toLocaleString("it-IT")} ore</b></> : null}</>
                : p ? <><b>{p.giorni}</b> giorni lavorativi (lun–sab) · {Number(f.num_operai) || 1} {Number(f.num_operai) === 1 ? "operaio" : "operai"} × {Number(f.ore_giorno) || 8} h = <b>{p.ore_previste.toLocaleString("it-IT")} ore</b> · fine <b>{fmtDay(p.data_fine)}</b></>
                : <span className="text-muted-foreground">Inserisci le date (o le ore) per vedere il calcolo.</span>}
            </div>
          </div>

          {operai.length > 0 && (
            <F label="Operai assegnati">
              <div className="flex flex-wrap gap-2" data-testid="cantiere-operai-picker">
                {operai.filter((o) => o.attivo !== false).map((o) => (
                  <button key={o.id} type="button" onClick={() => toggleOp(o.id)} data-testid={`cantiere-operaio-${o.id}`}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${f.operai_ids.includes(o.id) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>{o.nome}</button>
                ))}
              </div>
            </F>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Importo lavori (€)"><Input type="number" min="0" step="0.01" value={f.importo_lavori} onChange={(e) => set({ importo_lavori: e.target.value })} data-testid="cantiere-amount-input" /></F>
            <F label="Stato">
              <Select value={f.stato} onValueChange={(stato) => set({ stato })}>
                <SelectTrigger data-testid="cantiere-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATI).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Note" className="sm:col-span-2"><Textarea value={f.note || ""} onChange={(e) => set({ note: e.target.value })} rows={2} data-testid="cantiere-notes-input" /></F>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving || !f.nome || !p} data-testid="cantiere-save-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva cantiere"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
